import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTimeIntervalValidationSchema } from '../../../../../helpers/validationSchemaHelpers';
import { useModal } from '../../../../../hooks/modal/useModal';
import { useAvailabilities } from '../../../../../hooks/settings/handover/useAvailabilites';
import { useAvailabilitiesAction } from '../../../../../hooks/settings/handover/useAvailabilitiesAction';
import { useValidationForm } from '../../../../../hooks/useValidationForm';
import type { FormDataIntervals } from '../../Calendar.types';
import {
  applyDateToAvailability,
  excludeIntervalsFromAvailability,
  filterOutOldAvailabilities,
  findDayOrDate,
  getNewSlots,
} from '../../Calendar.utils';
import IntervalInputField from './IntervalInputField';

export const EditDateModal: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDate, availabilities, currentAvailabilities } =
    useAvailabilities();
  const { updateAvailabilities } = useAvailabilitiesAction();

  const isCurrentDayHoliday = useMemo(() => {
    if (!selectedDate) {
      return;
    }
    const availabilityIndex = findDayOrDate(availabilities, selectedDate);

    if (availabilityIndex === -1) {
      return false;
    }

    return availabilities[availabilityIndex]?.isHoliday || false;
  }, [availabilities, selectedDate]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    validationErrors,
    clearErrors,
  } = useValidationForm<FormDataIntervals>({
    schema: getTimeIntervalValidationSchema(),
    defaultValues: {
      newAvailabilities: currentAvailabilities,
      isHoliday: isCurrentDayHoliday,
    },
  });

  const { hideModal } = useModal();

  const hideAndSubmit = useCallback(
    async (data: FormDataIntervals) => {
      const { newAvailabilities, isHoliday = false } = data;

      if (!selectedDate) {
        return;
      }

      const newSlots = getNewSlots(newAvailabilities);

      // TODO: Rewrite to functional: availabilities.map(sortExcludedIntervals).map(applyToDate)
      const availabilityWithNoExcludedIntervals =
        excludeIntervalsFromAvailability(
          availabilities,
          newAvailabilities,
          selectedDate,
        );

      const availabilitiesWithAppliedDate = applyDateToAvailability(
        availabilityWithNoExcludedIntervals,
        newSlots,
        isHoliday,
        selectedDate,
      );

      const filteredDates = filterOutOldAvailabilities(
        availabilitiesWithAppliedDate,
      );

      await updateAvailabilities(filteredDates);
      hideModal();
    },
    [availabilities, hideModal, selectedDate, updateAvailabilities],
  );

  const isHoliday = watch('isHoliday');

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          {!isHoliday && (
            <div className='flex row items-center'>
              <IntervalInputField
                clearErrors={clearErrors}
                errors={validationErrors || {}}
                control={control}
                name={'newAvailabilities'}
                watch={watch}
                setValue={setValue}
              />
            </div>
          )}
          <CheckboxTogglerT
            toggleSize='large'
            label={t('handover-settings.holiday')}
            field='isHoliday'
            control={control}
          />
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};

export default EditDateModal;
